import React, {
  ChangeEvent, useEffect, useMemo, useRef, useState,
} from "react"
import {
  AspectRatio, Box, HStack, Image, Stack, Text,
} from "@chakra-ui/react"
import { useQueryClient } from "react-query"
import * as api from "api/websites"
import { updateWebsite } from "context/actions"
import Button from "components/Buttons/Button"
import CancelButton from "components/Buttons/CancelButton"
import Confirm from "components/dialogs/Confirm/Confirm"
import Modal from "components/dialogs/Modal"
import Icon from "components/elements/Icon"

type EventImageProps = {
  eventId: string
  coverImageUrl?: string
  canEdit: boolean
}

const EventImage = ({ eventId, coverImageUrl, canEdit }: EventImageProps) => {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)

  const chooseFile = () => fileInputRef.current?.click()

  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const previewUrl = useMemo(
    () => (pendingFile ? URL.createObjectURL(pendingFile) : null),
    [pendingFile],
  )

  useEffect(() => () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const pickFile = (event: ChangeEvent<HTMLInputElement>) => {
    setPendingFile(event.target.files?.[0] ?? null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const closePreview = () => {
    if (!isUploading) {
      setPendingFile(null)
    }
  }

  const uploadImage = () => {
    if (!pendingFile) {
      return
    }
    setIsUploading(true)
    api.putWebsite(eventId, { coverImage: pendingFile })
      .then(({ data }) => {
        updateWebsite(queryClient, eventId, data)
        setPendingFile(null)
      })
      .catch(() => {})
      .finally(() => setIsUploading(false))
  }

  const removeImage = () => api.putWebsite(eventId, { coverImage: null })
    .then(({ data }) => updateWebsite(queryClient, eventId, data))

  return (
    <>
      <AspectRatio ratio={3 / 2}>
        <Box borderWidth={1} borderRadius="md" overflow="hidden" bg="gray.50">
          {canEdit && (
          <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={pickFile} />
          )}
          {coverImageUrl ? (
            <>
              <Image
                alt="Event Image"
                w="full"
                h="full"
                objectFit="cover"
                src={`${coverImageUrl}?fit=crop&w=1080&h=720`}
              />
              {canEdit && (
              <HStack position="absolute" right={3} bottom={3} spacing={2}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="button.light"
                  leftIcon="email-block-image"
                  onClick={chooseFile}
                >
                  Replace
                </Button>
                <Confirm
                  confirmationTitle="Remove Event Image"
                  confirmationText="The image is removed from the event and its web page."
                  confirmButtonLabel="Remove"
                  confirmButtonColorScheme="button.danger"
                  onClickConfirm={removeImage}
                >
                  <Button size="sm" variant="outline" colorScheme="button.danger" leftIcon="times">
                    Remove
                  </Button>
                </Confirm>
              </HStack>
              )}
            </>
          ) : (
            <Stack align="center" spacing={3} color="gray.500">
              <Icon icon="email-block-image" size={10} />
              <Text>No event image yet</Text>
              {canEdit && (
              <Button size="sm" variant="outline" onClick={chooseFile}>
                Upload
              </Button>
              )}
            </Stack>
          )}
        </Box>
      </AspectRatio>
      <Modal
        size="lg"
        show={pendingFile !== null}
        hide={closePreview}
        title="Event Image"
        actions={[
          <CancelButton key="cancel" onClick={closePreview} isDisabled={isUploading} />,
          <Button key="save" onClick={uploadImage} isLoading={isUploading}>Save</Button>,
        ]}
      >
        <Stack spacing={4}>
          <Text>This will be the primary image on your event website.</Text>
          {previewUrl && (
            <Image src={previewUrl} alt="New event image" w="full" maxH="sm" objectFit="contain" borderRadius="md" />
          )}
        </Stack>
      </Modal>
    </>
  )
}

export default EventImage
